import { create } from 'zustand';

import type {
  CreateExpenseDto,
  ExpenseResponseDto,
  UpdateExpenseDto,
} from '@splitfiesta/shared';

import {
  EXAMPLE_GROUP_ID,
  type ExampleGroup,
  computeBalances,
  suggestSettlements,
} from './exampleGroup';
import type { ExampleScenario } from './exampleScenarios';

/**
 * Expenses the user adds to the example group during this launch. Only the
 * API client's example layer writes here (`withExampleGroup` answers the
 * example's expense create/update/delete from it); reads merge it over the
 * fixture. Each add belongs to the scenario it was made in and shows only
 * there, so a switch away and back finds it again. In memory on purpose: a
 * new launch starts every scenario clean.
 */

type ScenarioKey = ExampleScenario['key'];

export interface ExampleAddedExpense {
  id: string;
  /** The scenario it was added in. */
  scenario: ScenarioKey;
  title: string;
  amount: number;
  currency: string;
  date: string;
  category?: string;
  subcategory?: string | null;
  notes?: string;
  /** Payer member ids, the user first. */
  payerIds: string[];
  splitMemberIds: string[];
  splitMethod: 'equal' | 'percentage' | 'shares' | 'custom';
  createdAt: string;
  updatedAt: string;
  /** The signed-in user who added it. */
  createdBy: string;
  // The amounts as entered, so a custom split or two payers still add up
  // in the balances and the detail screen.
  payers: CreateExpenseDto['payers'];
  splits: CreateExpenseDto['splits'];
  originalAmount?: number;
  originalCurrency?: string;
  exchangeRate?: number;
}

export type KeepOffer = 'untouched' | 'dismissed' | 'kept';

export interface ExampleSandboxState {
  /** Every scenario's adds this launch; read one scenario's with `exampleAddsIn`. */
  added: ExampleAddedExpense[];
  keepOffer: KeepOffer;
  add(
    dto: CreateExpenseDto,
    userId: string,
    scenario: ScenarioKey
  ): ExampleAddedExpense;
  update(id: string, dto: UpdateExpenseDto): ExampleAddedExpense | undefined;
  remove(id: string): boolean;
  setKeepOffer(v: KeepOffer): void;
  /** A keep made the group real: every scenario's adds go, the offer is spent. */
  markKept(): void;
  /** Launch: every scenario's adds and the keep offer. */
  reset(): void;
}

export const EXAMPLE_ADDED_PREFIX = 'example-added-';

// Never rewinds on reset: a reused id could match a detail query that
// outlived the reset by a frame and show the previous row.
let addedCounter = 0;

function youFirst<T extends { userId: string }>(rows: T[], userId: string) {
  return [
    ...rows.filter(r => r.userId === userId),
    ...rows.filter(r => r.userId !== userId),
  ];
}

/** The fields an edit can change, applied the way the API's update does. */
export function applyExampleUpdate(
  e: ExampleAddedExpense,
  dto: UpdateExpenseDto
): ExampleAddedExpense {
  const payers = dto.payers ? youFirst(dto.payers, e.createdBy) : e.payers;
  const splits = dto.splits ?? e.splits;
  const next: ExampleAddedExpense = {
    ...e,
    ...(dto.title !== undefined ? { title: dto.title } : {}),
    ...(dto.amount !== undefined ? { amount: dto.amount } : {}),
    ...(dto.currency !== undefined ? { currency: dto.currency } : {}),
    ...(dto.date !== undefined ? { date: dto.date } : {}),
    ...(dto.category !== undefined ? { category: dto.category } : {}),
    ...(dto.subcategory !== undefined ? { subcategory: dto.subcategory } : {}),
    ...(dto.splitMethod !== undefined ? { splitMethod: dto.splitMethod } : {}),
    payers,
    splits,
    payerIds: payers.map(p => p.userId),
    splitMemberIds: splits.map(s => s.userId),
    updatedAt: new Date().toISOString(),
  };
  if (dto.notes !== undefined) {
    if (dto.notes) next.notes = dto.notes;
    else delete next.notes;
  }
  if (dto.originalCurrency === null) {
    delete next.originalCurrency;
    delete next.originalAmount;
    delete next.exchangeRate;
  } else if (dto.originalCurrency !== undefined) {
    next.originalCurrency = dto.originalCurrency;
    if (dto.originalAmount !== undefined)
      next.originalAmount = dto.originalAmount;
    if (dto.exchangeRate != null) next.exchangeRate = dto.exchangeRate;
  }
  return next;
}

const NO_ADDS: ExampleAddedExpense[] = [];

/** One scenario's adds, in the order they were made. */
export function exampleAddsIn(
  added: ExampleAddedExpense[],
  scenario: ScenarioKey
): ExampleAddedExpense[] {
  const adds = added.filter(e => e.scenario === scenario);
  return adds.length === 0 ? NO_ADDS : adds;
}

const initial = {
  added: [] as ExampleAddedExpense[],
  keepOffer: 'untouched' as KeepOffer,
};

export const useExampleSandboxStore = create<ExampleSandboxState>()(set => ({
  ...initial,
  add: (dto, userId, scenario) => {
    addedCounter += 1;
    const now = new Date().toISOString();
    const payers = youFirst(dto.payers, userId);
    const expense: ExampleAddedExpense = {
      id: `${EXAMPLE_ADDED_PREFIX}${addedCounter}`,
      scenario,
      title: dto.title,
      amount: dto.amount,
      currency: dto.currency,
      date: dto.date,
      ...(dto.category !== undefined ? { category: dto.category } : {}),
      ...(dto.subcategory !== undefined
        ? { subcategory: dto.subcategory }
        : {}),
      ...(dto.notes ? { notes: dto.notes } : {}),
      payerIds: payers.map(p => p.userId),
      splitMemberIds: dto.splits.map(s => s.userId),
      splitMethod: dto.splitMethod,
      createdAt: now,
      createdBy: userId,
      updatedAt: now,
      payers,
      splits: dto.splits,
      ...(dto.originalCurrency
        ? {
            originalCurrency: dto.originalCurrency,
            ...(dto.originalAmount !== undefined
              ? { originalAmount: dto.originalAmount }
              : {}),
            ...(dto.exchangeRate !== undefined
              ? { exchangeRate: dto.exchangeRate }
              : {}),
          }
        : {}),
    };
    set(state => ({ added: [...state.added, expense] }));
    return expense;
  },
  update: (id, dto) => {
    let updated: ExampleAddedExpense | undefined;
    set(state => ({
      added: state.added.map(e => {
        if (e.id !== id) return e;
        updated = applyExampleUpdate(e, dto);
        return updated;
      }),
    }));
    return updated;
  },
  remove: id => {
    let removed = false;
    set(state => {
      const added = state.added.filter(e => e.id !== id);
      removed = added.length !== state.added.length;
      return { added };
    });
    return removed;
  },
  setKeepOffer: keepOffer => set({ keepOffer }),
  markKept: () => set({ keepOffer: 'kept', added: [] }),
  reset: () => set({ ...initial }),
}));

export function resetExampleSandbox(): void {
  useExampleSandboxStore.getState().reset();
}

/**
 * Only an add the user alone paid, in the example's own currency, can
 * travel into a kept group as is: anyone else paying or a converted amount
 * would need people and a rate the new group does not have yet.
 */
export function qualifiesForKeep(
  e: ExampleAddedExpense,
  userId: string,
  exampleCurrency: string
): boolean {
  return (
    e.payerIds.length === 1 &&
    e.payerIds[0] === userId &&
    e.currency === exampleCurrency &&
    (e.originalCurrency === undefined || e.originalCurrency === exampleCurrency)
  );
}

function newestFirst(a: ExampleAddedExpense, b: ExampleAddedExpense): number {
  return (
    b.createdAt.localeCompare(a.createdAt) ||
    Number(b.id.slice(EXAMPLE_ADDED_PREFIX.length)) -
      Number(a.id.slice(EXAMPLE_ADDED_PREFIX.length))
  );
}

export function latestQualifyingAdd(
  state: Pick<ExampleSandboxState, 'added'>,
  userId: string,
  exampleCurrency: string
): ExampleAddedExpense | undefined {
  return [...state.added]
    .sort(newestFirst)
    .find(e => qualifiesForKeep(e, userId, exampleCurrency));
}

// ---------------------------------------------------------------- validation

export const EXAMPLE_VALIDATION_ERROR = 'VALIDATION_ERROR';

/** The API's 400 for a body it refuses, in the shape both apps' error readers take. */
export class ExampleSandboxValidationError extends Error {
  readonly code = EXAMPLE_VALIDATION_ERROR;
  readonly status = 400;
  readonly response: {
    status: number;
    data: {
      success: false;
      error: { code: string; message: string; translationKey: string };
    };
  };

  constructor(reason: string) {
    super('Validation failed');
    this.name = 'ExampleSandboxValidationError';
    this.response = {
      status: 400,
      data: {
        success: false,
        error: {
          code: EXAMPLE_VALIDATION_ERROR,
          message: `Validation failed: ${reason}`,
          translationKey: 'errors:validation.failed',
        },
      },
    };
  }
}

const CENT = 0.01 + 1e-9;

function sumOf(rows: { amount: number }[]): number {
  return rows.reduce((a, r) => a + r.amount, 0);
}

/** Throws the API-shaped 400 when the server would refuse this expense. */
export function validateExampleExpense(
  e: Pick<ExampleAddedExpense, 'title' | 'amount' | 'payers' | 'splits'>,
  memberIds: string[]
): void {
  const fail = (reason: string) => {
    throw new ExampleSandboxValidationError(reason);
  };
  if (!(typeof e.amount === 'number' && e.amount > 0)) fail('amount');
  if (!e.title?.trim()) fail('title');
  if (!e.splits?.length) fail('splits');
  if (!e.payers?.length) fail('payers');
  const isMember = (id: string) => memberIds.includes(id);
  if (!e.payers.every(p => isMember(p.userId))) fail('payers');
  if (!e.splits.every(s => isMember(s.userId))) fail('splits');
  if (Math.abs(sumOf(e.splits) - e.amount) > CENT) fail('splits');
  if (Math.abs(sumOf(e.payers) - e.amount) > CENT) fail('payers');
}

// ---------------------------------------------------------------- merge

const round2 = (n: number) => Math.round(n * 100) / 100;

export function toExampleExpenseDto(
  e: ExampleAddedExpense,
  ex: ExampleGroup
): ExpenseResponseDto {
  const name = (id: string) => ex.members.find(m => m.id === id)?.name ?? '';
  const createdBy = ex.group.createdBy;
  return {
    id: e.id,
    groupId: EXAMPLE_GROUP_ID,
    title: e.title,
    amount: e.amount,
    currency: e.currency,
    ...(e.category ? { category: e.category } : {}),
    ...(e.subcategory ? { subcategory: e.subcategory } : {}),
    date: e.date,
    ...(e.notes ? { notes: e.notes } : {}),
    splitMethod: e.splitMethod,
    ...(e.originalCurrency
      ? {
          originalCurrency: e.originalCurrency,
          ...(e.originalAmount !== undefined
            ? { originalAmount: e.originalAmount }
            : {}),
          ...(e.exchangeRate !== undefined
            ? { exchangeRate: e.exchangeRate }
            : {}),
        }
      : {}),
    createdBy,
    createdByName: name(createdBy),
    createdAt: e.createdAt,
    updatedAt: e.updatedAt,
    payers: e.payers.map(p => ({
      userId: p.userId,
      userName: name(p.userId),
      amount: p.amount,
    })),
    splits: e.splits.map(s => ({
      userId: s.userId,
      userName: name(s.userId),
      amount: s.amount,
      ...(s.percentage !== undefined ? { percentage: s.percentage } : {}),
      ...(s.shares !== undefined ? { shares: s.shares } : {}),
    })),
  };
}

/** The sandbox's adds, newest first, ahead of the fixture's own rows. */
export function mergeExpenses(
  ex: ExampleGroup,
  added: ExampleAddedExpense[]
): ExpenseResponseDto[] {
  return [
    ...[...added].sort(newestFirst).map(e => toExampleExpenseDto(e, ex)),
    ...ex.expenses,
  ];
}

/** Balances over every expense shown plus the fixture's recorded payment. */
export function sandboxBalances(
  ex: ExampleGroup,
  expenses: ExpenseResponseDto[]
): Record<string, number> {
  const s = ex.settlement;
  const balance = computeBalances(
    ex.members.map(m => m.id),
    expenses,
    [
      {
        fromUserId: s.paidBy?.[0]?.userId ?? '',
        toUserId: s.splits?.[0]?.userId ?? '',
        amount: s.amount,
      },
    ]
  );
  return Object.fromEntries(
    Object.entries(balance).map(([id, b]) => [id, round2(b)])
  );
}

/** The example as it reads with this launch's adds in its scenario. */
export function withSandbox(
  ex: ExampleGroup,
  allAdded: ExampleAddedExpense[]
): ExampleGroup {
  const added = exampleAddsIn(allAdded, ex.scenario);
  if (added.length === 0) return ex;
  const expenses = mergeExpenses(ex, added);
  const balance = sandboxBalances(ex, expenses);
  // Cent rounding leaves sub-cent remainders the settle-up walk would turn
  // into payments of 0.00.
  const suggestions = suggestSettlements(balance)
    .map(s => ({ ...s, amount: round2(s.amount) }))
    .filter(s => s.amount > 0);
  const totalSpent = round2(expenses.reduce((a, e) => a + e.amount, 0));
  return {
    ...ex,
    expenses,
    suggestions,
    group: {
      ...ex.group,
      expenseCount: expenses.length,
      recentExpenses: expenses,
      userBalance: balance[ex.group.createdBy],
      balances: ex.group.balances.map(b => ({
        ...b,
        balance: balance[b.userId],
      })),
      dashboard: {
        ...ex.group.dashboard,
        currency: ex.group.currency,
        totalSpent,
        avgPerMember: totalSpent / ex.members.length,
      },
    },
  };
}
